import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, BoxProps, Button, Center, Heading, HStack, Icon, LinkBox, SimpleGrid, SimpleGridProps, Spinner, StackProps, Text, VStack } from '@chakra-ui/react';
import { ButtonLink } from '@src/components/sco/ButtonLink.tsx';
import { InvestorSearchCard, InvestorSearchCardProps } from '@src/components/sco/InvestorMatching/InvestorSearchCard';
import { AddIcon } from '@src/components/sco/icons.tsx';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import React from 'react';
import { LuBadgeDollarSign, LuChevronRight, LuPlus } from 'react-icons/lu';
type InvestorSearchesListProps = {
  investorSearchCardProps?: Partial<InvestorSearchCardProps>;
  stackProps?: StackProps;
  resultsSimpleGridProps?: SimpleGridProps;
  title?: string;
  showCreateCard?: boolean;
  hideIfEmpty?: boolean;
  hideTitle?: boolean;
  renderEmpty?: () => any;
  excludeEmptyResults?: boolean;
  wrapperProps?: BoxProps;
};
export const InvestorSearchesList: React.FC<InvestorSearchesListProps> = props => {
  const $ = _c(40);
  const {
    currentUser
  } = useCurrentUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = gql`
      query ($searchingUserId: String, $excludeEmptyResults: Boolean) {
        scoInvestorSearches(searchingUserId: $searchingUserId, excludeEmptyResults: $excludeEmptyResults) {
          id
          items {
            id
            investorSearchId
          }
        }
      }
    `;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = !currentUser?.userId;
  const t2 = currentUser?.userId;
  let t3;
  if ($[1] !== props.excludeEmptyResults || $[2] !== t2) {
    t3 = {
      excludeEmptyResults: props.excludeEmptyResults,
      searchingUserId: t2
    };
    $[1] = props.excludeEmptyResults;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t3) {
    t4 = {
      fetchPolicy: "network-only",
      ssr: false,
      skip: t1,
      variables: t3
    };
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const query = useQuery(t0, t4);
  const investorSearches = query.data?.scoInvestorSearches?.items;
  if (!currentUser?.userId) {
    return null;
  }
  if (props.hideIfEmpty && (query.loading || investorSearches?.length === 0)) {
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <></>;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  if (props.renderEmpty && investorSearches?.length === 0) {
    let t5;
    if ($[8] !== props.renderEmpty) {
      t5 = props.renderEmpty();
      $[8] = props.renderEmpty;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t5) {
      t6 = <>{t5}</>;
      $[10] = t5;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  let t5;
  if ($[12] !== props.hideTitle || $[13] !== props.title) {
    t5 = !props.hideTitle && <HStack justify="space-between" paddingY={2}><HStack><Icon asChild={true}><LuBadgeDollarSign /></Icon><Heading size="sm">{props.title || "Your Fundraises"}</Heading></HStack><Box><ButtonLink href="/investors/matching/start" size="xs" variant="outline"><AddIcon />Create a Fundraise</ButtonLink></Box></HStack>;
    $[12] = props.hideTitle;
    $[13] = props.title;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== query.loading) {
    t6 = query.loading && <Center bgColor="blackAlpha.300" borderRadius="md" padding={4}><Spinner /></Center>;
    $[15] = query.loading;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== investorSearches) {
    t7 = !investorSearches && <Center bgColor="blackAlpha.300" borderRadius="md" padding={4}><Text>Error: Investor Searches not loaded.</Text></Center>;
    $[17] = investorSearches;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== investorSearches?.length) {
    t8 = investorSearches?.length == 0 && <VStack bgColor="blackAlpha.300" borderRadius="md" padding={4}><Text>No searches yet.</Text><Button asChild={true} variant="outlineGhost" width="full"><NextLink href="/investors/matching/start">Get Started<LuChevronRight /></NextLink></Button></VStack>;
    $[19] = investorSearches?.length;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== investorSearches || $[22] !== props.investorSearchCardProps) {
    t9 = investorSearches?.map((investorSearch, idx) => <InvestorSearchCard key={idx} investorSearchId={investorSearch.investorSearchId} {...props.investorSearchCardProps} />);
    $[21] = investorSearches;
    $[22] = props.investorSearchCardProps;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== props.showCreateCard) {
    t10 = props.showCreateCard && <LinkBox asChild={true} alignItems="center" bgColor="colorPalette.subtle" border="1px solid transparent" borderRadius="md" display="flex" flexDirection="column" justifyContent="center" paddingY={4} _hover={{
      boxShadow: "md",
      borderColor: "colorPalette.border",
      color: "colorPalette.border"
    }}><NextLink href="/investors/matching/start"><Icon asChild={true}><LuPlus /></Icon><Text>New Fundraise</Text></NextLink></LinkBox>;
    $[24] = props.showCreateCard;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== props.resultsSimpleGridProps || $[27] !== t10 || $[28] !== t9) {
    t11 = <SimpleGrid columns={1} gap={2} {...props.resultsSimpleGridProps}>{t9}{t10}</SimpleGrid>;
    $[26] = props.resultsSimpleGridProps;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== props.stackProps || $[31] !== t11 || $[32] !== t5 || $[33] !== t6 || $[34] !== t7 || $[35] !== t8) {
    t12 = <VStack align="stretch" {...props.stackProps}>{t5}{t6}{t7}{t8}{t11}</VStack>;
    $[30] = props.stackProps;
    $[31] = t11;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== props.wrapperProps || $[38] !== t12) {
    t13 = <Box {...props.wrapperProps}>{t12}</Box>;
    $[37] = props.wrapperProps;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  return t13;
};